import type {
  AiGateway,
  CompletionChunk,
  CompletionRequest,
  CompletionResult,
  KeyVault,
  LLMProvider,
  ProviderProfile,
  Transport,
} from "@roqeet/core";
import { DirectTransport, ProxyTransport } from "./transport.js";
import { OpenAiProvider } from "./providers/openai.js";
import { AnthropicProvider } from "./providers/anthropic.js";
import { GoogleProvider } from "./providers/google.js";

/**
 * AI 게이트웨이 파사드 — 결정 #7. 앱이 호출하는 단일 진입점.
 *  - 프로필의 `kind`로 알맞은 Provider 어댑터를 고르고,
 *  - 프로필의 `transport`로 전송 방식(direct/proxy/gateway)을 정하며,
 *  - `keyRef`를 키 볼트(결정 #15)에서 평문 키로 호출 시점에만 해석한다.
 *
 * Provider와 Transport를 분리했기에(결정 #7), BYO-key(direct+사용자키)와
 * 유료 게이트웨이(gateway+세션토큰)가 앱 로직 변경 없이 같은 인터페이스로 흐른다.
 */
export interface AiGatewayOptions {
  /** ProxyTransport가 사용할 엣지 프록시 베이스 URL(결정 #7). */
  proxyBaseUrl?: string;
}

export class WebAiGateway implements AiGateway {
  constructor(
    private readonly vault: KeyVault,
    private readonly options: AiGatewayOptions = {},
  ) {}

  private providerFor(profile: ProviderProfile): LLMProvider {
    switch (profile.kind) {
      case "anthropic":
        return new AnthropicProvider();
      case "google":
        return new GoogleProvider();
      case "openai":
      case "openrouter":
      case "openai-compatible":
      case "platform":
      default:
        // platform(유료 게이트웨이)도 OpenAI 호환 형식으로 가정(결정 #7).
        return new OpenAiProvider(profile.kind);
    }
  }

  private transportFor(profile: ProviderProfile): Transport {
    if (profile.transport === "proxy") {
      const base = profile.proxyUrl ?? this.options.proxyBaseUrl;
      if (!base) {
        throw new Error(
          "프록시 전송이 선택됐지만 프록시 URL이 없습니다. 설정에서 프록시 URL을 지정하세요.",
        );
      }
      return new ProxyTransport(base);
    }
    // gateway는 세션 토큰을 쓰지만 전송 자체는 direct와 동일 경로로 단순화.
    return new DirectTransport();
  }

  /** 프로필의 keyRef를 평문 키로 해석(호출 시점, 결정 #15). */
  private async resolveKey(profile: ProviderProfile): Promise<string | undefined> {
    if (!profile.keyRef) return undefined;
    return this.vault.resolve(profile.keyRef);
  }

  async complete(req: CompletionRequest, profile: ProviderProfile): Promise<CompletionResult> {
    const provider = this.providerFor(profile);
    const transport = this.transportFor(profile);
    const apiKey = await this.resolveKey(profile);
    const prepared = provider.prepare({ ...req, stream: false }, profile, apiKey);
    const res = await transport.send(prepared);
    return provider.parseResult(res);
  }

  async *stream(req: CompletionRequest, profile: ProviderProfile): AsyncIterable<CompletionChunk> {
    const provider = this.providerFor(profile);
    const transport = this.transportFor(profile);
    const apiKey = await this.resolveKey(profile);
    const prepared = provider.prepare({ ...req, stream: true }, profile, apiKey);
    const res = await transport.send(prepared);
    if (!res.ok) {
      // 스트림 경로에서도 에러 본문을 노출해 디버깅을 돕는다.
      const text = (await res.text?.()) ?? "";
      throw new Error(`AI 스트리밍 실패 (HTTP ${res.status}): ${text.slice(0, 500)}`);
    }
    yield* provider.parseStream(res);
  }

  async countTokens(text: string, profile: ProviderProfile): Promise<number> {
    const provider = this.providerFor(profile);
    return provider.countTokens(text, profile.defaultModel ?? "");
  }

  /** 프로필이 지원하면 모델 목록을 조회(설정 UI에서 사용). 키는 볼트에서 해석. */
  async listModels(profile: ProviderProfile): Promise<string[]> {
    const provider = this.providerFor(profile);
    const apiKey = await this.resolveKey(profile);
    const transport = this.transportFor(profile);
    const viaTransport = await this.listModelsViaTransport(profile, apiKey, transport).catch(() => undefined);
    if (viaTransport !== undefined) return viaTransport;
    if (!provider.listModels) return [];
    return provider.listModels(profile, apiKey);
  }

  /**
   * 명시적 키로 모델 목록을 조회한다(프로바이더 추가 폼 전용 — 아직 볼트에
   * 저장되지 않은 키로도 미리 모델을 불러올 수 있게).
   */
  async listModelsWithKey(profile: ProviderProfile, apiKey?: string): Promise<string[]> {
    const provider = this.providerFor(profile);
    const transport = this.transportFor(profile);
    const viaTransport = await this.listModelsViaTransport(profile, apiKey, transport).catch(() => undefined);
    if (viaTransport !== undefined) return viaTransport;
    if (!provider.listModels) return [];
    return provider.listModels(profile, apiKey);
  }

  private async listModelsViaTransport(
    profile: ProviderProfile,
    apiKey: string | undefined,
    transport: Transport,
  ): Promise<string[] | undefined> {
    switch (profile.kind) {
      case "anthropic":
        return this.listAnthropicModels(profile, apiKey, transport);
      case "openai":
      case "openrouter":
      case "openai-compatible":
      case "platform":
        return this.listOpenAiCompatibleModels(profile, apiKey, transport);
      case "google":
        return this.listGoogleModels(profile, apiKey, transport);
      default:
        return undefined;
    }
  }

  private async listOpenAiCompatibleModels(
    profile: ProviderProfile,
    apiKey: string | undefined,
    transport: Transport,
  ): Promise<string[]> {
    const headers: Record<string, string> = { ...(profile.extraHeaders ?? {}) };
    if (apiKey) headers["authorization"] = `Bearer ${apiKey}`;
    if (profile.kind === "openrouter") {
      headers["http-referer"] = headers["http-referer"] ?? "https://roqeet.com";
      headers["x-title"] = headers["x-title"] ?? "RoQeet Launcher";
    }
    const res = await transport.send({
      url: `${openAiCompatibleBaseUrl(profile)}/models`,
      method: "GET",
      headers,
    });
    if (!res.ok) return [];
    const json = JSON.parse((await res.text?.()) ?? "{}") as { data?: { id?: string }[] };
    return unique((json.data ?? []).map((model) => model.id).filter(isString)).sort();
  }

  private async listAnthropicModels(
    profile: ProviderProfile,
    apiKey: string | undefined,
    transport: Transport,
  ): Promise<string[]> {
    const models: string[] = [];
    let afterId: string | undefined;
    for (let page = 0; page < 20; page++) {
      const query = new URLSearchParams({ limit: "1000" });
      if (afterId) query.set("after_id", afterId);
      const headers: Record<string, string> = {
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
        ...(profile.extraHeaders ?? {}),
      };
      if (apiKey) headers["x-api-key"] = apiKey;
      const res = await transport.send({
        url: `${anthropicBaseUrl(profile)}/models?${query.toString()}`,
        method: "GET",
        headers,
      });
      if (!res.ok) return unique(models);
      const json = JSON.parse((await res.text?.()) ?? "{}") as {
        data?: { id?: string }[];
        has_more?: boolean;
        last_id?: string;
      };
      models.push(...(json.data ?? []).map((model) => model.id).filter(isString));
      if (!json.has_more || !json.last_id) break;
      afterId = json.last_id;
    }
    return unique(models);
  }

  private async listGoogleModels(
    profile: ProviderProfile,
    apiKey: string | undefined,
    transport: Transport,
  ): Promise<string[]> {
    const models: string[] = [];
    let pageToken: string | undefined;
    for (let page = 0; page < 20; page++) {
      const query = new URLSearchParams({ pageSize: "1000" });
      if (apiKey) query.set("key", apiKey);
      if (pageToken) query.set("pageToken", pageToken);
      const res = await transport.send({
        url: `${googleBaseUrl(profile)}/models?${query.toString()}`,
        method: "GET",
        headers: { ...(profile.extraHeaders ?? {}) },
      });
      if (!res.ok) return unique(models).sort();
      const json = JSON.parse((await res.text?.()) ?? "{}") as {
        models?: { name?: string; supportedGenerationMethods?: string[]; supportedActions?: string[]; supported_actions?: string[] }[];
        nextPageToken?: string;
      };
      for (const model of json.models ?? []) {
        const id = model.name?.replace(/^models\//, "");
        const methods = model.supportedGenerationMethods ?? model.supportedActions ?? model.supported_actions ?? [];
        if (id && (methods.length === 0 || methods.includes("generateContent"))) models.push(id);
      }
      if (!json.nextPageToken) break;
      pageToken = json.nextPageToken;
    }
    return unique(models).sort();
  }
}

function openAiCompatibleBaseUrl(profile: ProviderProfile): string {
  if (profile.baseUrl) return profile.baseUrl.replace(/\/+$/, "");
  if (profile.kind === "openrouter") return "https://openrouter.ai/api/v1";
  return "https://api.openai.com/v1";
}

function anthropicBaseUrl(profile: ProviderProfile): string {
  return (profile.baseUrl ?? "https://api.anthropic.com/v1").replace(/\/+$/, "");
}

function googleBaseUrl(profile: ProviderProfile): string {
  return (profile.baseUrl ?? "https://generativelanguage.googleapis.com/v1beta").replace(/\/+$/, "");
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function isString(value: string | undefined): value is string {
  return typeof value === "string" && value.length > 0;
}
